import React, { useEffect } from 'react';
import { ROOMS } from '../../rooms';
import { useWorldStore } from '../../state/worldStore';
import { useHypeU, loadCourses, classroomId, ensureClassroom, lobbyId, rideTo, courseOf, stepOut, MOVE_MS } from '../../city/hypeu';
import { useNarrow } from '../SceneChat';

// THE ELEVATOR'S BUTTONS (Hype-U, DOM). Khari: "When they step into the
// elevator, the floor buttons are labeled after the course title."
//
// Aboard (you walked into the car and the elevator took the camera:
// ElevatorDirector), a brass panel lists the floors: every course — its title,
// and who teaches it — numbered by its floor, the one you are on lit; and under
// them, always, LOBBY (2026-11-01, Khari: "We need a button in the elevator for
// 'lobby'"): pinned below the list so it never scrolls away, lit as "you are
// here" in the lobby, the way down from every classroom. A press rides there (city/hypeu rideTo); while it does the screen
// dims and says where you are going. "Step out" gives you the camera back
// without riding. Set down by a ride, the floors wait behind one button
// ("▲▼ Floors", which hands the elevator the camera again) until you step out.
const GOLD = '#d8a640';
const NAVY = 'rgba(18,26,51,0.94)';

export default function ElevatorPanel() {
  const interior = useWorldStore((s) => s.interior);
  const room = ROOMS[interior];
  const inCar = useHypeU((s) => s.inCar);
  const arrivedIn = useHypeU((s) => s.arrivedIn);
  const aboard = useHypeU((s) => s.aboard);
  const ride = useHypeU((s) => s.ride);
  const courses = useHypeU((s) => s.courses);
  const narrow = useNarrow();
  const here = !!room?.hypeu && !!room?.elevator;

  useEffect(() => { if (here) loadCourses(); }, [here]);

  // The ride: the screen dims while the car moves, and says where to.
  if (ride) {
    const to = ROOMS[ride.to];
    const dim = ride.phase === 'moving';
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 90, pointerEvents: 'none', background: '#05070d', opacity: dim ? 0.86 : 0, transition: `opacity ${dim ? 450 : 600}ms ease` }}>
        {dim && (
          <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', color: '#f7f3ea', fontFamily: 'Georgia, serif' }}>
            <div style={{ font: '700 13px system-ui', letterSpacing: 3, color: GOLD, textTransform: 'uppercase' }}>{to?.hypeu === 'lobby' ? 'Going down' : 'Going up'}</div>
            <div style={{ fontSize: 28, marginTop: 8 }}>{to?.course?.title || (to?.hypeu === 'lobby' ? 'Lobby' : to?.name || '')}</div>
            <div style={{ width: 160, height: 3, margin: '14px auto 0', background: 'rgba(216,166,64,0.25)', overflow: 'hidden', borderRadius: 2 }}>
              <div style={{ height: '100%', width: '100%', background: GOLD, transformOrigin: 'left', animation: `huRide ${MOVE_MS}ms linear forwards` }} />
            </div>
            <style>{'@keyframes huRide { from { transform: scaleX(0) } to { transform: scaleX(1) } }'}</style>
          </div>
        )}
      </div>
    );
  }
  if (!here || !inCar) return null;

  const wrap = narrow
    ? { position: 'fixed', left: 12, right: 12, bottom: 'calc(max(14px, env(safe-area-inset-bottom)) + var(--hud-player-h, 0px))', zIndex: 72 }
    : { position: 'fixed', right: 24, top: '50%', transform: 'translateY(-50%)', width: 320, zIndex: 72 };
  const card = { ...wrap, pointerEvents: 'auto', background: NAVY, border: `1px solid ${GOLD}66`, borderRadius: 16, padding: '14px 14px 12px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', fontFamily: 'system-ui, sans-serif', color: '#f7f3ea', maxHeight: narrow ? '52dvh' : '78vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' };

  if (!aboard) {
    if (!arrivedIn) return null;   // the elevator is taking you aboard
    return (
      <div style={{ ...wrap, pointerEvents: 'auto', width: 'auto', left: narrow ? '50%' : 'auto', right: narrow ? 'auto' : 24, transform: narrow ? 'translateX(-50%)' : 'translateY(-50%)' }}>
        <button type="button" onClick={() => useHypeU.setState({ arrivedIn: false })} style={{ cursor: 'pointer', padding: '9px 16px', borderRadius: 999, border: `1px solid ${GOLD}88`, background: NAVY, color: GOLD, font: '700 13px system-ui', letterSpacing: 0.4 }}>
          ▲▼ Floors
        </button>
      </div>
    );
  }

  const current = courseOf(interior)?.slug || null;
  // Numbered by the course's place in the list — the same floor city/hypeu
  // stands that classroom on, so the number is the same from every car.
  const floors = (courses || []).map((c, i) => ({ id: classroomId(c.slug), slug: c.slug, n: i + 1, title: c.title, sub: c.host?.name ? `with ${c.host.name}` : '', course: c }));
  const inLobby = room.hypeu === 'lobby';
  const floorBtn = (f, on, badge) => (
    <button
      key={f.id}
      type="button"
      disabled={on}
      onClick={() => { if (f.course) ensureClassroom(f.course); rideTo(f.id); }}
      style={{
        flex: 'none', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', cursor: on ? 'default' : 'pointer', width: '100%',
        padding: '9px 11px', borderRadius: 12, border: `1px solid ${on ? GOLD : 'rgba(216,166,64,0.32)'}`,
        background: on ? 'rgba(216,166,64,0.18)' : 'rgba(255,255,255,0.04)', color: '#f7f3ea',
      }}
    >
      <span style={{
        flex: 'none', width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center',
        border: `2px solid ${GOLD}`, color: on ? '#121a33' : GOLD, background: on ? GOLD : 'transparent', font: '800 12px system-ui',
        boxShadow: on ? `0 0 12px ${GOLD}` : 'none',
      }}>{badge}</span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', font: '700 14.5px system-ui' }}>{f.title}</span>
        {f.sub && <span style={{ display: 'block', fontSize: 11.5, opacity: 0.62, marginTop: 1 }}>{f.sub}</span>}
      </span>
    </button>
  );
  return (
    <div style={card} role="dialog" aria-label="Elevator">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ font: '700 11px system-ui', letterSpacing: 3, color: GOLD, textTransform: 'uppercase' }}>Hype-U · Floors</div>
        <div style={{ fontSize: 11, opacity: 0.6 }}>{room.hypeu === 'class' ? (room.course?.title || '') : 'Lobby'}</div>
      </div>
      {courses === null && <div style={{ fontSize: 13, opacity: 0.7, padding: '6px 2px' }}>Finding the classes…</div>}
      {courses?.length === 0 && inLobby && <div style={{ fontSize: 13, opacity: 0.7, padding: '6px 2px' }}>No classes are open right now.</div>}
      {/* Only the courses scroll: Lobby and Step out stay put under them. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {floors.map((f) => floorBtn(f, f.slug === current, f.n))}
      </div>
      <div style={{ flex: 'none', marginTop: 9, paddingTop: 9, borderTop: '1px solid rgba(216,166,64,0.25)' }}>
        {floorBtn({ id: lobbyId(), title: 'Lobby', sub: inLobby ? 'You are here' : 'Hype-U · the way out to the street' }, inLobby, 'L')}
      </div>
      <button type="button" onClick={stepOut} style={{ flex: 'none', marginTop: 10, width: '100%', cursor: 'pointer', padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(247,243,234,0.25)', background: 'transparent', color: 'rgba(247,243,234,0.8)', font: '600 12.5px system-ui' }}>
        Step out
      </button>
    </div>
  );
}
